import {DistinctDomainKeyWithChainIndex} from 'antibodies/AntibodyTypedefs';
import {CodonTranslationTable} from 'biojs/BioConstants';

export enum AntibodyCreationStepKey {
  SELECT_FORMAT = 'SELECT_FORMAT',
  DEFINE_DOMAINS = 'DEFINE_DOMAINS',
  FINALIZE = 'FINALIZE',
}

export const ANTIBODY_CREATION_STEPS = [
  {key: AntibodyCreationStepKey.SELECT_FORMAT, title: 'Select format & components'},
  {key: AntibodyCreationStepKey.DEFINE_DOMAINS, title: 'Configure components'},
  {key: AntibodyCreationStepKey.FINALIZE, title: 'Finalize & create'},
];

export interface SelectFormatPane {
  paneKey: AntibodyCreationStepKey.SELECT_FORMAT;
}

export interface GetDomainsPane {
  paneKey: AntibodyCreationStepKey.DEFINE_DOMAINS;
}

export interface FinalizePane {
  paneKey: AntibodyCreationStepKey.FINALIZE;
}

export type Pane = SelectFormatPane | GetDomainsPane | FinalizePane;

// The state representing the progress of and configuration specified in the nested data creation modal
export interface AntibodyNestedDataCreationState {
  // The registry API ID
  selectedRegistryId: string | null;
  // The format API ID of antibodies to be created
  selectedFormatId: string | null;
  // The schema ID for the antibodies to be created
  selectedSchemaId: string | null;
  // The type of nested data that the antibody creation will be derived from
  startingDataType: NestedDataType;
  // The configuration describing existing entities that are used in nested data creation
  inputConfig: NestedDataCreationInputConfig;
  // Types of entities to create from nested data creation, aside from antibodies which are always created
  outputTypes: ReadonlySet<NestedDataType>;
  // The configuration for the entities that will be created as part of nested data creation
  outputConfig: NestedDataCreationOutputConfig;
}

// The types of data that can be created from or used as a starting point in nested data creation
export enum NestedDataType {
  DNA_DOMAIN = 'DNA_DOMAIN',
  AA_DOMAIN = 'AA_DOMAIN',
  AA_CHAIN = 'AA_CHAIN',
}

interface NestedDataCreationInputConfig {
  variableDnaDomains: VariableDnaDomainInputConfig;
  constantAaDomains: ConstantAaDomainInputConfig;
}

interface NestedDataCreationOutputConfig {
  antibodies: AntibodyCreateConfig;
  variableAaDomains: VariableAaDomainCreateConfig;
}

/** Configuration interfaces for nested data creation inputs */

// We currently only support creating variable domains from pairs but may support others in the future
export enum DomainSourceType {
  DNA_PAIR = 'DNA_PAIR',
}

interface VariableDnaDomainInputConfig {
  sourceType: DomainSourceType.DNA_PAIR;
  pairSchemaId: string | null;
  pairIds: Array<string>;
}

export type ConstantAaDomainEntity = DistinctDomainKeyWithChainIndex & {aaSequenceId: string | null};

interface ConstantAaDomainInputConfig {
  // Keys are the stringified version of the distinct domain type and specificity
  entities: Record<string, ConstantAaDomainEntity>;
  schemaId: string | null;
}

/** Configuration interfaces for entities that will be newly created as part of nested data creation */

interface VariableAaDomainCreateConfig {
  translationParameters: TranslationParameters;
}

interface AntibodyCreateConfig {
  schemaId: string | null;
  folderId: string | null;
}

export interface TranslationParameters {
  geneticCode: CodonTranslationTable;
}

export const INITIAL_ANTIBODY_NDC_TRANSLATION_PARAMETERS: TranslationParameters = {
  geneticCode: CodonTranslationTable.STANDARD,
};

export const INITIAL_ANTIBODY_NESTED_DATA_CREATION_STATE: AntibodyNestedDataCreationState = {
  selectedRegistryId: null,
  selectedFormatId: null,
  selectedSchemaId: null,
  startingDataType: NestedDataType.DNA_DOMAIN,
  inputConfig: {
    variableDnaDomains: {
      sourceType: DomainSourceType.DNA_PAIR,
      pairSchemaId: null,
      pairIds: [],
    },
    constantAaDomains: {
      entities: {},
      schemaId: null,
    },
  },
  outputTypes: new Set([NestedDataType.AA_DOMAIN, NestedDataType.AA_CHAIN]),
  outputConfig: {
    antibodies: {
      schemaId: null,
      folderId: null,
    },
    variableAaDomains: {
      translationParameters: INITIAL_ANTIBODY_NDC_TRANSLATION_PARAMETERS,
    },
  },
};

export const MAX_ANTIBODIES_TO_CREATE = 250;
export const DNA_PAIR_TAG_SCHEMA_SYSTEM_NAME = 'dna_pair';
// Only formats with a single variable pair are currently supported in NDC
export const ALLOWED_ANTIBODY_FORMAT_IDS = [
  'builtinabfmt_Fab',
  'builtinabfmt_IgGmono',
  'builtinabfmt_scFv',
  'builtinabfmt_TCR',
];
